/** Mirrors para-node `src/constants/partner-invoice.constants.ts`. */
export enum PartnerInvoiceStatus {
  Created = 'created',
  Pending = 'pending',
  Canceled = 'canceled',
  Funded = 'funded',
  RepaymentPending = 'repayment_pending',
  Repaid = 'repaid',
}

export enum PartnerInvoiceType {
  Wire = 'wire',
  Loan = 'loan',
  // The admin list endpoint filters these out server-side, so it never arrives on this screen.
  // Kept for completeness because the wire type is shared with other surfaces.
  Historical = 'historical',
}

export enum PartnerInvoiceExternalStatus {
  New = 'NEW',
  Accepted = 'ACCEPTED',
  Declined = 'DECLINED',
  PartiallyPaid = 'PARTIALLY_PAID',
  PaidInFull = 'PAID_IN_FULL',
  PaymentScheduled = 'PAYMENT_SCHEDULED',
}

export enum PartnerInvoiceFundingStatus {
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Processed = 'PROCESSED',
  Succeeded = 'SUCCEEDED',
  Failed = 'FAILED',
  Corrected = 'CORRECTED',
  Canceled = 'CANCELED',
  Error = 'ERROR',
}

/**
 * Same member set as `PartnerInvoiceFundingStatus` today, but a distinct enum in para-node
 * (`OatfiPaymentStatusEnum` in `src/constants/oatfi.constants.ts`) describing a different
 * column. Kept separate rather than aliased — the two can diverge, and a shared alias would
 * silently mistype `paymentStatus` the day one of them gains a member.
 */
export enum OatfiPaymentStatus {
  Pending = 'PENDING',
  Processing = 'PROCESSING',
  Processed = 'PROCESSED',
  Succeeded = 'SUCCEEDED',
  Failed = 'FAILED',
  Corrected = 'CORRECTED',
  Canceled = 'CANCELED',
  Error = 'ERROR',
}

/**
 * The seven money actions. The value is an internal key only — it is never concatenated into a
 * URL. `INVOICE_ACTION_ENDPOINTS` maps each verb to its `Endpoints` member.
 */
export enum InvoiceActionVerb {
  Cancel = 'cancel',
  MarkFunded = 'markFunded',
  MarkFundedNoCheck = 'markFundedNoCheck',
  ProcessPayment = 'processPayment',
  MarkRepaid = 'markRepaid',
  RepayFees = 'repayFees',
  RepayDueInterest = 'repayDueInterest',
}

/**
 * Derived client-side from the nested partner invoices — the composed-invoice DTO carries no
 * status of its own. See `deriveComposedInvoiceStatus`.
 */
export enum ComposedInvoiceStatus {
  Canceled = 'canceled',
  FundingFailed = 'fundingFailed',
  FundedAndPaid = 'fundedAndPaid',
  Repaying = 'repaying',
  AwaitingFunding = 'awaitingFunding',
}

export enum ComposedInvoicesTableFields {
  invoice = 'invoice',
  type = 'type',
  status = 'status',
  amount = 'amount',
  lateFee = 'lateFee',
  generated = 'generated',
  actions = 'actions',
}

/** Declaration order is column order; the first nine are visible by default. */
export enum PartnerInvoicesTableFields {
  invoice = 'invoice',
  status = 'status',
  type = 'type',
  amount = 'amount',
  paidAmount = 'paidAmount',
  invoiceDate = 'invoiceDate',
  dueDate = 'dueDate',
  earningsCount = 'earningsCount',
  actions = 'actions',
  externalStatus = 'externalStatus',
  loanFundingStatus = 'loanFundingStatus',
  loanFundingErrorMessage = 'loanFundingErrorMessage',
  paymentStatus = 'paymentStatus',
  paymentErrorMessage = 'paymentErrorMessage',
  paymentDate = 'paymentDate',
  payInvoiceOnDate = 'payInvoiceOnDate',
  loanId = 'loanId',
  loanDueDate = 'loanDueDate',
  loanTotalAmount = 'loanTotalAmount',
  loanBaseFeeAmount = 'loanBaseFeeAmount',
  loanTransferFee = 'loanTransferFee',
  loanTransferRate = 'loanTransferRate',
  wireTransferFee = 'wireTransferFee',
  wireTransferRate = 'wireTransferRate',
  gigsafeTotalFeeAmount = 'gigsafeTotalFeeAmount',
  invoiceApprovedByPayor = 'invoiceApprovedByPayor',
  payorExternalId = 'payorExternalId',
  payeeExternalId = 'payeeExternalId',
  paymentId = 'paymentId',
  description = 'description',
}
